import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LOGO_EMBLEM, scrollTo } from "@/lib/site";

const LINKS = [
  ["Home", "#home"],
  ["Services", "#services"],
  ["Pricing", "#pricing"],
  ["Our Work", "#work"],
  ["Process", "#process"],
  ["Why Falkeen", "#why"],
  ["Contact", "#contact"],
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-white/10 bg-black/60 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-12">
        <a href="#home" onClick={(e) => go(e, "#home")} data-testid="nav-logo" className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-white shadow-[0_0_20px_rgba(0,229,255,0.25)]">
            <img src={LOGO_EMBLEM} alt="Falkeen Technologies" className="h-8 w-8 object-contain" />
          </span>
          <span className="font-display text-sm font-extrabold uppercase tracking-[0.18em] text-white">
            Falkeen<span className="text-accent"> Technologies</span>
          </span>
        </a>

        <ul className="hidden items-center gap-7 lg:flex">
          {LINKS.map(([label, hash]) => (
            <li key={hash}>
              <a
                href={hash}
                onClick={(e) => go(e, hash)}
                data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 transition-colors duration-300 hover:text-white"
              >
                {label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-accent transition-[width] duration-300 group-hover:w-full" />
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            onClick={(e) => go(e, "#enquiry")}
            data-testid="nav-get-started-btn"
            className="group hidden items-center gap-1.5 rounded-full bg-accent px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,box-shadow] duration-300 hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] sm:flex"
          >
            Get Started
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-btn"
            aria-label="Toggle menu"
            className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
          >
            <span className={`h-0.5 w-6 bg-white transition-transform duration-300 ${open ? "translate-y-1 rotate-45" : ""}`} />
            <span className={`h-0.5 w-6 bg-white transition-transform duration-300 ${open ? "-translate-y-1 -rotate-45" : ""}`} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/10 bg-black/90 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <ul className="space-y-1 px-6 py-6">
              {LINKS.map(([label, hash], i) => (
                <motion.li
                  key={hash}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <a
                    href={hash}
                    onClick={(e) => go(e, hash)}
                    data-testid={`nav-mobile-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block py-2.5 font-display text-lg font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:text-accent"
                  >
                    {label}
                  </a>
                </motion.li>
              ))}
              <li className="pt-3">
                <button
                  onClick={(e) => go(e, "#enquiry")}
                  data-testid="nav-mobile-get-started-btn"
                  className="w-full rounded-full bg-accent py-3 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink"
                >
                  Get Started
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
